<template>
  <div v-if="hasAnyPermission('user-view')" class="py-4 container-fluid">
    <ToastPrime position="bottom-left" />
    <div class="card">
      <div
        class="card-header bg-white py-3 d-flex flex-wrap align-items-center justify-content-between gap-3 border-bottom">
        <div class="d-flex align-items-center gap-2">
          <h5 class="mb-0 fw-bold">Manage Account</h5>
        </div>
        <div></div>
        <div class="d-flex gap-2">
          <button type="button" class="mx-1 mb-0 btn btn-sm btn-outline-primary" @click="openSearchDialog">
            <i class="pi pi-search me-1"></i> Search
          </button>
          <button v-if="hasActiveFilters" type="button" class="mx-1 mb-0 btn btn-sm btn-outline-secondary" @click="resetSearch">
            <i class="pi pi-times me-1"></i> Clear
          </button>
          <button v-if="hasAnyPermission('user-create')" type="button" class="mx-1 mb-0 btn btn-sm btn-primary" @click="goToAdd">Add</button>
        </div>
      </div>
      <div style="height: 500px; overflow-y: auto;">
        <DataTable
:value="table.onLoad ? dummyRows : table.filteredData" removable-sort scrollable scroll-height="flex"
          table-style="min-width: 50rem">
          <Column field="username" header="Username" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.username ? slotProps.data.username : '' }}
            </template>
          </Column>
          <Column field="name" header="Name" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.name ? slotProps.data.name : '' }}
            </template>
          </Column>
          <Column field="email" header="Email" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.email ? slotProps.data.email : '' }}
            </template>
          </Column>
          <Column field="company_code" header="Company Code" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.company_code ? slotProps.data.company_code : '' }}
            </template>
          </Column>
          <Column field="department" header="Departement" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data?.user_sections?.length
                ? slotProps.data.user_sections.map(s => s.section_detail?.description || s.section_code).join(', ')
                : (slotProps.data?.department || '') }}
            </template>
          </Column>
          <Column field="status" header="Status" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              <ArgonSwitch
:id="'switch-' + slotProps.data.id" :name="'switch-' + slotProps.data.id"
                :checked="slotProps.data.status === 'active'" :input-class="'form-check-input-sm'"
                :disabled="switchLoadingId === slotProps.data.id || !hasAnyPermission('user-update-status')" @change="() => updateStatus(slotProps.data)" />
              <span v-if="switchLoadingId === slotProps.data.id" class="ms-2"><i
                  class="pi pi-spin pi-spinner"></i></span>
            </template>
          </Column>
          <Column
header="Actions" header-class="bg-light" :style="{ width: '150px' }"
            :body-style="{ width: '150px', padding: '0' }">
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              <ButtonPrime
v-if="hasAnyPermission('user-update')" v-tooltip.bottom="'Update'" icon="pi pi-pencil" outlined rounded size="small"
                class="me-2" @click="editAccount(slotProps.data.id)" />
              <ButtonPrime
v-if="hasAnyPermission('user-delete')" v-tooltip.bottom="'Delete'" icon="pi pi-trash" outlined rounded
                severity="danger" size="small" @click="deleteAccount(slotProps.data.id)" />
            </template>
          </Column>
          <template #empty>
            <div style="text-align: center; padding: 40px 0;">
              <img src="/no_data.png" alt="No Data" style="max-width: 330px; opacity: 0.7; margin-bottom: 0;" />
              <div style="color: #888; font-size: 1.1em; font-weight: 500;">
                No Data
              </div>
            </div>
          </template>
        </DataTable>
      </div>
      <div class="card-footer pb-0 d-flex justify-content-between align-items-center">
        <BasePagination
v-model="table.page.current_pages" :page-count="table.page.total_pages || 1" size="sm"
          @click="changePage()" />
        <p>
          {{ (table.page.current_pages - 1) * table.page.per_pages + (table.filteredData ? table.filteredData.length :
            0) }}
          of {{ table.total }}
        </p>
      </div>
    </div>

    <!-- Dialog Filter (PrimeVue) -->
    <DialogPrime
v-model:visible="searchDialog.visible" modal header="Search"
      style="width: 25rem; margin: 10px;">
      <div class="mb-2">
        <label for="search_keyword" class="font-semibold">Name</label>
        <InputText id="search_keyword" v-model="searchForm.keyword" placeholder="Search by username or name" class="w-100" />
      </div>
      <div class="mb-2">
        <label for="search_email" class="font-semibold">Email</label>
        <InputText id="search_email" v-model="searchForm.email" placeholder="Email" class="w-100" />
      </div>
      <div class="mb-2">
        <label for="search_company" class="font-semibold">Company</label>
        <Dropdown
id="search_company" v-model="searchForm.company_code" :options="companyOptions" option-label="label"
          option-value="value" placeholder="Select Company" class="w-100" show-clear />
      </div>
      <div class="mb-2">
        <label for="search_section" class="font-semibold">Department</label>
        <MultiSelect
id="search_section" v-model="searchForm.section" :options="sectionOptions" option-label="label"
          option-value="value" filter placeholder="Search Department (min. 2 characters)" class="w-100" display="chip" show-clear
          :loading="sectionLoading" @show="loadSectionOptions" @filter="searchSectionOptions" />
      </div>
      <div class="mb-2">
        <label for="search_status" class="font-semibold">Status</label>
        <Dropdown
id="search_status" v-model="searchForm.status" :options="statusOptions" option-label="label"
          option-value="value" placeholder="Select Status" class="w-100" />
      </div>
      <div class="d-flex justify-content-end gap-2 mt-3">
        <ButtonPrime type="button" label="Cancel" severity="secondary" @click="closeSearchDialog"></ButtonPrime>
        <ButtonPrime type="button" label="Search" @click="doAdvancedSearch" />
      </div>
    </DialogPrime>
  </div>
  <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import BasePagination from "@/components/BasePagination.vue";
import api from "@/helpers/api";
import manageAccountService from "@/services/manageAccount/manageAccountService";
import ArgonSwitch from "@/components/ArgonSwitch.vue";
import moment from "moment";
import companiesService from "@/services/generalMasterData/companiesService";
import generalMasterDataService from "@/services/generalMasterData/generalMasterDataService";
import NoAccess from "@/components/NoAccess.vue";

export default {
  components: { BasePagination, ArgonSwitch, NoAccess },
  computed: {
    ...mapState({
      authUserPermissions: (state) => state.auth.userHasPermissions || [],
    }),
    hasActiveFilters() {
      return Object.keys(this.table.search).length > 0;
    },
    clientCode() {
      const userAuth = JSON.parse(localStorage.getItem('userAuth') || '{}');
      return userAuth?.user?.client_code || userAuth?.user?.company_code || '';
    },
  },
  data() {
    return {
      table: {
        onLoad: true,
        search: {},
        total: 0,
        data: [],
        filteredData: [],
        limit: 10,
        page: {
          current_pages: 1,
          per_pages: 10,
          total_pages: 1,
        },
        loadTimeout: null,
      },
      dummyRows: Array.from({ length: 10 }, () => ({})),
      switchLoadingId: null,
      searchDialog: {
        visible: false,
      },
      searchForm: {
        keyword: '',
        email: '',
        company_code: '',
        section: [],
        status: '',
      },
      sectionOptions: [],
      allSectionOptions: [],
      sectionLoading: false,
      statusOptions: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
      ],
      companyOptions: [],
    };
  },
  mounted() {
    this.loadFromUrl();
    if (this.hasAnyPermission('user-view')) {
      this.get();
      this.getCompanies();
    }
  },
  methods: {
    hasAnyPermission(...perms) {
      return perms.some((p) => this.authUserPermissions.includes(p));
    },
    loadFromUrl() {
      const query = this.$route.query;
      if (Object.keys(query).length > 0) {
        this.table.search = {
          search: query.search || '',
          email: query.email || '',
          company_code: query.company_code || '',
          section: this.toArray(query.section),
          status: query.status || ''
        };
        this.table.page.current_pages = parseInt(query.page) || 1;
      }
    },
    goToAdd() {
      this.$router.push({ name: 'Account List Create', params: { moduleSlug: 'material-management' } });
    },
    async updateStatus(account) {
      this.switchLoadingId = account.id;
      const newStatus = account.status === 'active' ? 'inactive' : 'active';
      try {
        await api(this, manageAccountService.updateStatus(account.id, { status: newStatus }));
        // Tidak perlu memanggil this.get() karena status diupdate langsung di UI
        account.status = newStatus;
      } catch (error) {
        // handle error (bisa tambahkan notifikasi)
      } finally {
        this.switchLoadingId = null;
      }
    },
    // async getCompanies() {
    //   try {
    //     const res = await api(this, companiesService.list({ limit: 0 }), false); // Use 0 for all or a large number
    //     this.companyOptions = (res.data.data || res.data || []).map((c) => ({
    //       label: c.label || c.code,
    //       value: c.code,
    //     }));
    //   } catch (error) {
    //     console.error("Failed to load companies:", error);
    //   }
    // },
    async get(paramsOverride) {
      this.table.onLoad = true;
      try {
        let params = {
          limit: this.table.limit,
          page: this.table.page.current_pages,
        };
        if (this.table.search && typeof this.table.search === 'object') {
          Object.assign(params, this.table.search);
        }
        const queryParams = paramsOverride || params;

        const response = await api(this, manageAccountService.list(queryParams), false);
        const paginated = response.data;
        this.table.data = paginated.data || [];
        this.table.total = paginated.total || 0;
        this.table.page.total_pages = paginated.last_page || 1;
        this.table.page.per_pages = paginated.per_page || this.table.limit;
        this.applyFilter();
      } catch (error) {
        this.table.data = [];
        this.table.filteredData = [];
        this.table.total = 0;
        this.table.page.total_pages = 1;
      } finally {
        this.table.onLoad = false;
      }
    },
    applyFilter() {
      this.table.filteredData = this.table.data;
    },
    changePage() {
      this.get();
    },
    formatDate(date) {
      return date ? moment(date).format("DD MMMM YYYY, HH:mm") : "-";
    },
    openSearchDialog() {
      this.searchDialog.visible = true;
      this.searchForm = {
        keyword: this.table.search?.search || '',
        email: this.table.search?.email || '',
        company_code: this.table.search?.company_code || '',
        section: this.toArray(this.table.search?.section),
        status: this.table.search?.status || '',
      };
      // Tampilkan nilai terpilih tanpa memanggil API (API baru dipanggil saat dropdown dibuka).
      this.searchForm.section.forEach((v) => this.ensureSelectedOption(this.sectionOptions, v));
    },
    closeSearchDialog() {
      this.searchDialog.visible = false;
    },
    doAdvancedSearch() {
      const searchObj = {};
      if (this.searchForm.keyword) searchObj.search = this.searchForm.keyword;
      if (this.searchForm.email) searchObj.email = this.searchForm.email;
      if (this.searchForm.company_code) searchObj.company_code = this.searchForm.company_code;
      if (this.searchForm.section?.length) searchObj.section = [...this.searchForm.section];
      if (this.searchForm.status) searchObj.status = this.searchForm.status;
      
      this.table.search = searchObj;
      this.searchDialog.visible = false;
      this.table.page.current_pages = 1;
      
      const query = { ...searchObj, page: 1 };
      this.$router.push({ query: Object.keys(query).length > 1 ? query : null });
      
      this.get();
    },
    async deleteAccount(id) {
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          api(this, manageAccountService.delete(id))
            .then(() => {
              this.get();
              this.$toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Account deleted successfully!',
                life: 3000
              });
            })
            .catch((error) => {
              this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to delete account!',
                life: 5000
              });
            });
        }
      });
    },
    resetFilter() {
      this.searchForm = {
        keyword: '',
        email: '',
        company_code: '',
        section: [],
        status: '',
      };
      this.table.search = {};
      this.$router.push({ query: null });
      this.searchDialog.visible = false;
      this.table.page.current_pages = 1;
      this.get();
    },
    editAccount(id) {
      this.$router.push({ name: 'Account List Edit', params: { moduleSlug: 'material-management', id } });
    },
    async getCompanies() {
      try {
        const res = await api(this, companiesService.list({ limit: 0 }), false);
        this.companyOptions = (res.data || []).map(c => ({
          label: `${c.code} - ${c.description}`,
          value: c.code
        }));
      } catch (error) {
        console.error('Error loading companies:', error);
      }
    },
    resetSearch() {
      this.table.search = {};
      this.$router.push({ query: null });
      this.table.page.current_pages = 1;
      this.get();
    },
    // Query URL bisa string (1 value) atau array (lebih dari 1 value).
    toArray(value) {
      if (Array.isArray(value)) return value.filter(Boolean);
      return value ? [value] : [];
    },
    mapOptions(list, query = '') {
      const options = new Map();
      (Array.isArray(list) ? list : []).forEach((item) => {
        const code = item?.code || '';
        const label = item?.description || code;
        if (code && !options.has(code)) options.set(code, { label, value: code });
      });
      let result = [...options.values()];
      if (query) result = this.filterOptionsByQuery(result, query);
      return result;
    },
    filterOptionsByQuery(options, query) {
      const q = query.toLowerCase();
      return options.filter(o => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
    },
    // Pastikan nilai terpilih tetap tampil walau tidak ketemu di opsi (mis. dari query URL).
    ensureSelectedOption(options, value) {
      if (!value) return;
      if (options.some(o => o.value === value)) return;
      options.unshift({ label: value, value });
    },
    async fetchSectionOptions(search, limit) {
      this.sectionLoading = true;
      try {
        const params = { limit, client: this.clientCode };
        if (search) params.search = search;
        const res = await api(this, generalMasterDataService.list('section', params), false);
        return this.mapOptions(res?.data?.data || res?.data || [], search);
      } catch {
        return [];
      } finally {
        this.sectionLoading = false;
      }
    },
    async loadSectionOptions() {
      const fetched = await this.fetchSectionOptions('', 10);
      this.sectionOptions = Array.isArray(fetched) ? fetched : [];
      this.allSectionOptions = [...this.sectionOptions];
      if (!Array.isArray(this.searchForm.section)) this.searchForm.section = [];
      this.searchForm.section.forEach((v) => this.ensureSelectedOption(this.sectionOptions, v));
    },
    // Search server-side: hit API begitu ketik min. 2 karakter.
    // Opsi terpilih (bisa lebih dari satu) wajib dipertahankan agar chip MultiSelect tidak hilang.
    async searchSectionOptions(event) {
      const query = (event?.value || '').trim();
      const selectedValues = this.searchForm.section || [];
      if (query.length < 2) {
        const base = [...this.allSectionOptions];
        selectedValues.forEach((v) => this.ensureSelectedOption(base, v));
        this.sectionOptions = base;
        return;
      }
      let options = await this.fetchSectionOptions(query, 10);
      // Kalau backend abaikan param `search` / balik kosong, saring daftar yang sudah dimuat.
      if (!options.length) options = this.filterOptionsByQuery(this.allSectionOptions, query);
      const optionValues = new Set(options.map((o) => o.value));
      const missingSelected = this.sectionOptions.filter((o) => selectedValues.includes(o.value) && !optionValues.has(o.value));
      this.sectionOptions = [...missingSelected, ...options];
    },
  },
};
</script>
